import type { RGBColor } from "react-color";

import * as React from "react";

import { PickerComponent } from "@common/designSystem/color/CustomPicker";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import {
  getFormattedColor,
  getFormattedColorWithoutOpacity,
  getHex8Color,
  getHexColor,
  getInitialColorValue,
  getRGB,
} from "@editor/utils/colors";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import { ToggleGroup } from "@replo/design-system/components/toggle/ToggleGroup";
import twMerge from "@replo/design-system/utils/twMerge";
import { ArrowLeft, ChevronUp, Pencil, Plus, SwatchBook } from "lucide-react";

interface DesignLibraryColorsProps {
  styleLibrary: {
    colors: {
      light: string;
      subtle: string;
      muted: string;
      dark: string;
      primary: string;
      secondary: string;
      tertiary: string;
      accent: string;
    };
    typography?: any; // Keep existing typography
  } | null;
  onBack?: () => void;
  onPresetsClick?: () => void;
}

export const DesignLibraryColors: React.FC<DesignLibraryColorsProps> = ({
  styleLibrary,
  onBack,
  onPresetsClick,
}) => {
  const projectId = useCurrentProjectId();
  const utils = trpcUtils;

  const { mutateAsync: updateStyleLibrary } =
    trpc.styles.createOrUpdateStyleLibrary.useMutation({
      onSuccess: () => {
        void utils.styles.getStyleLibrary.invalidate();
      },
    });

  const colorsFromProjectStyleLibrary = styleLibrary?.colors;

  const [expandedColor, setExpandedColor] = React.useState<string | null>(null);
  const [pickerType, setPickerType] = React.useState<"solid" | "gradient">(
    "solid",
  );

  const [pickerColor, setPickerColor] = React.useState<RGBColor>(
    getInitialColorValue(null),
  );

  const [hexInputValue, setHexInputValue] = React.useState<string>("#000000");

  const handleColorChange = React.useCallback((rgb: RGBColor) => {
    setPickerColor(rgb);
    setHexInputValue(getFormattedColor(getHexColor(rgb)) ?? "#000000");
  }, []);

  const handleColorCommit = React.useCallback(async () => {
    if (!expandedColor || !projectId) {
      return;
    }

    const newColorValue = getFormattedColor(getHex8Color(pickerColor));
    if (!newColorValue) {
      return;
    }

    const updatedColors = {
      light: colorsFromProjectStyleLibrary?.light ?? "",
      subtle: colorsFromProjectStyleLibrary?.subtle ?? "",
      muted: colorsFromProjectStyleLibrary?.muted ?? "",
      dark: colorsFromProjectStyleLibrary?.dark ?? "",
      primary: colorsFromProjectStyleLibrary?.primary ?? "",
      secondary: colorsFromProjectStyleLibrary?.secondary ?? "",
      tertiary: colorsFromProjectStyleLibrary?.tertiary ?? "",
      accent: colorsFromProjectStyleLibrary?.accent ?? "",
      [expandedColor]: newColorValue,
    };

    await updateStyleLibrary({
      projectId,
      styleLibrary: {
        colors: updatedColors,
        typography: styleLibrary?.typography,
      },
    });
  }, [
    expandedColor,
    pickerColor,
    projectId,
    colorsFromProjectStyleLibrary,
    styleLibrary?.typography,
    updateStyleLibrary,
  ]);

  const handleHexInputCommit = React.useCallback(() => {
    const newColor = getRGB(hexInputValue);
    if (newColor) {
      setPickerColor(newColor);
      void handleColorCommit();
    } else {
      setHexInputValue(
        getFormattedColor(getHexColor(pickerColor)) ?? "#000000",
      );
    }
  }, [hexInputValue, pickerColor, handleColorCommit]);

  const neutralColors = [
    {
      name: "Light",
      key: "light",
      value: colorsFromProjectStyleLibrary?.light ?? "#FFFFFF",
    },
    {
      name: "Subtle",
      key: "subtle",
      value: colorsFromProjectStyleLibrary?.subtle ?? "#FFFFFF",
    },
    {
      name: "Muted",
      key: "muted",
      value: colorsFromProjectStyleLibrary?.muted ?? "#FFFFFF",
    },
    {
      name: "Dark",
      key: "dark",
      value: colorsFromProjectStyleLibrary?.dark ?? "#FFFFFF",
    },
  ];

  const brandColorKeys = [
    "primary",
    "secondary",
    "tertiary",
    "accent",
  ] as const;

  // Transform brand colors from object to array format for UI rendering
  const brandColorsFromProjectStyleLibrary = brandColorKeys
    .filter(
      (key) =>
        colorsFromProjectStyleLibrary?.[key] &&
        colorsFromProjectStyleLibrary[key].trim() !== "",
    )
    .map((key) => ({
      name: key.charAt(0).toUpperCase() + key.slice(1),
      key,
      value: colorsFromProjectStyleLibrary![key],
    }));

  const hasAllBrandColors = brandColorKeys.every(
    (key) =>
      colorsFromProjectStyleLibrary?.[key] &&
      colorsFromProjectStyleLibrary[key].trim() !== "",
  );

  const handleEditColor = (colorKey: string) => {
    const newExpandedColor = expandedColor === colorKey ? null : colorKey;
    setExpandedColor(newExpandedColor);

    if (newExpandedColor) {
      const colorValue =
        colorsFromProjectStyleLibrary?.[
          newExpandedColor as keyof typeof colorsFromProjectStyleLibrary
        ] ?? "#FFFFFF";
      const newColor = getRGB(colorValue);
      setPickerColor(newColor);
      setHexInputValue(getFormattedColor(getHexColor(newColor)) ?? "#000000");
    }
  };

  const handleAddBrandColor = async () => {
    if (!projectId || hasAllBrandColors) {
      return;
    }

    const nextColorKey = brandColorKeys.find(
      (key) =>
        !colorsFromProjectStyleLibrary?.[key] ||
        colorsFromProjectStyleLibrary[key].trim() === "",
    );

    if (!nextColorKey) {
      return;
    }

    const updatedColors = {
      light: colorsFromProjectStyleLibrary?.light ?? "",
      subtle: colorsFromProjectStyleLibrary?.subtle ?? "",
      muted: colorsFromProjectStyleLibrary?.muted ?? "",
      dark: colorsFromProjectStyleLibrary?.dark ?? "",
      primary: colorsFromProjectStyleLibrary?.primary ?? "",
      secondary: colorsFromProjectStyleLibrary?.secondary ?? "",
      tertiary: colorsFromProjectStyleLibrary?.tertiary ?? "",
      accent: colorsFromProjectStyleLibrary?.accent ?? "",
      [nextColorKey]: "#FFFFFF",
    };

    await updateStyleLibrary({
      projectId,
      styleLibrary: {
        colors: updatedColors,
        typography: styleLibrary?.typography,
      },
    });

    setExpandedColor(nextColorKey);
  };

  return (
    <div className="flex flex-col items-start flex-[1_0_0] self-stretch min-h-0 rounded-lg border border-border bg-white shadow-sm">
      <div className="flex justify-between items-center self-stretch p-6 border-b-0.5 border-border">
        <div className="flex items-center gap-3">
          <IconButton
            variant="secondary"
            size="sm"
            layoutClassName="h-6 w-6"
            icon={<ArrowLeft size={16} className="text-default" />}
            onClick={() => {
              if (projectId) {
                void utils.webContainer.getFileTree.invalidate({ projectId });
              }
              onBack?.();
            }}
          />
          <div className="text-lg font-semibold">Colors</div>
        </div>
        <Button
          variant="secondary"
          size="sm"
          startEnhancer={<SwatchBook size={14} />}
          onClick={onPresetsClick}
        >
          Presets
        </Button>
      </div>
      <div className="flex flex-1 flex-col self-stretch overflow-y-auto no-scrollbar">
        <div className="flex flex-col gap-4 px-6 py-4 border-b-0.5 border-border">
          <div className="text-sm font-medium text-muted">Neutrals</div>
          <div className="flex flex-col">
            {neutralColors.map((color) => (
              <div
                key={color.key}
                className={twMerge(
                  "group transition-colors cursor-pointer",
                  expandedColor === color.key
                    ? "rounded-lg border border-border bg-white shadow-sm p-2"
                    : "rounded-lg border border-transparent hover:bg-sheet p-2",
                )}
                onClick={() => {
                  if (expandedColor !== color.key) {
                    handleEditColor(color.key);
                  }
                }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={twMerge(
                      "w-21 rounded-lg border-0.5 border-black/10 flex-shrink-0 transition-all duration-300 ease-in-out",
                      expandedColor === color.key ? "h-85" : "h-12",
                    )}
                    style={{
                      backgroundColor:
                        expandedColor === color.key
                          ? getFormattedColor(getHex8Color(pickerColor)) ??
                            color.value
                          : color.value,
                    }}
                  />
                  <div className="flex flex-col flex-1">
                    <div
                      className={twMerge(
                        "flex flex-col",
                        expandedColor === color.key &&
                          "border-b-0.5 border-border pb-2",
                      )}
                    >
                      <div className="text-base font-medium">{color.name}</div>
                      <div className="text-base text-muted uppercase">
                        {getFormattedColorWithoutOpacity(color.value)}
                      </div>
                    </div>
                    <div
                      className={twMerge(
                        "grid overflow-hidden transition-[grid-template-rows,opacity,padding-top] duration-300 ease-in-out",
                        expandedColor === color.key
                          ? "pt-4 opacity-100 grid-rows-[1fr]"
                          : "pt-0 opacity-0 grid-rows-[0fr]",
                      )}
                    >
                      <div className="flex flex-col gap-4 min-h-0">
                        {/* Color Picker Tabs */}
                        <div className="flex gap-1">
                          <ToggleGroup
                            options={[
                              { label: "Solid", value: "solid" },
                              { label: "Gradient", value: "gradient" },
                            ]}
                            selectedValue={pickerType}
                            onChange={(value: string) =>
                              setPickerType(value as "solid" | "gradient")
                            }
                            size="sm"
                            UNSAFE_className="rounded-md"
                          />
                        </div>

                        {/* Main Color Picker Area */}
                        <PickerComponent
                          color={pickerColor}
                          onChange={({ rgb }) => handleColorChange(rgb)}
                          onDragEnd={() => void handleColorCommit()}
                        />

                        {/* Hex Input */}
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted">Hex</span>
                          <input
                            type="text"
                            value={hexInputValue}
                            onChange={(e) => setHexInputValue(e.target.value)}
                            onBlur={handleHexInputCommit}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                handleHexInputCommit();
                              }
                            }}
                            className="flex-1 px-3 py-2 text-sm border border-border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <IconButton
                    variant="secondary"
                    size="sm"
                    layoutClassName={twMerge(
                      "h-8 w-8",
                      expandedColor === color.key
                        ? "self-start"
                        : "self-center",
                    )}
                    UNSAFE_className="opacity-0 group-hover:opacity-100 transition-opacity"
                    icon={
                      expandedColor === color.key ? (
                        <ChevronUp size={14} className="text-default" />
                      ) : (
                        <Pencil size={14} className="text-default" />
                      )
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditColor(color.key);
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-4 px-6 py-4 ">
          <div className="text-sm font-medium text-muted">Brand Colors</div>
          <div className="flex flex-col">
            {brandColorsFromProjectStyleLibrary.map((color) => (
              <div
                key={color.key}
                className={twMerge(
                  "group transition-colors cursor-pointer",
                  expandedColor === color.key
                    ? "rounded-lg border border-border bg-white shadow-sm p-2"
                    : "rounded-lg border border-transparent hover:bg-sheet p-2",
                )}
                onClick={() => {
                  if (expandedColor !== color.key) {
                    handleEditColor(color.key);
                  }
                }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={twMerge(
                      "w-21 rounded-lg border-0.5 border-black/10 flex-shrink-0 transition-all duration-300 ease-in-out",
                      expandedColor === color.key ? "h-85" : "h-12",
                    )}
                    style={{
                      backgroundColor:
                        expandedColor === color.key
                          ? getFormattedColor(getHex8Color(pickerColor)) ??
                            color.value
                          : color.value,
                    }}
                  />
                  <div className="flex flex-col flex-1">
                    <div
                      className={twMerge(
                        "flex flex-col",
                        expandedColor === color.key &&
                          "border-b-0.5 border-border pb-2",
                      )}
                    >
                      <div className="text-base font-medium">{color.name}</div>
                      <div className="text-base text-muted">
                        {color.name} •{" "}
                        {getFormattedColorWithoutOpacity(
                          color.value,
                        )?.toUpperCase()}
                      </div>
                    </div>
                    <div
                      className={twMerge(
                        "grid overflow-hidden transition-[grid-template-rows,opacity,padding-top] duration-300 ease-in-out",
                        expandedColor === color.key
                          ? "pt-4 opacity-100 grid-rows-[1fr]"
                          : "pt-0 opacity-0 grid-rows-[0fr]",
                      )}
                    >
                      <div className="flex flex-col gap-4 min-h-0">
                        {/* Color Picker Tabs */}
                        <div className="flex gap-1">
                          <ToggleGroup
                            options={[
                              { label: "Solid", value: "solid" },
                              { label: "Gradient", value: "gradient" },
                            ]}
                            selectedValue={pickerType}
                            onChange={(value: string) =>
                              setPickerType(value as "solid" | "gradient")
                            }
                            size="sm"
                            UNSAFE_className="rounded-md"
                          />
                        </div>

                        {/* Main Color Picker Area */}
                        <PickerComponent
                          color={pickerColor}
                          onChange={({ rgb }) => handleColorChange(rgb)}
                          onDragEnd={() => void handleColorCommit()}
                        />

                        {/* Hex Input */}
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted">Hex</span>
                          <input
                            type="text"
                            value={hexInputValue}
                            onChange={(e) => setHexInputValue(e.target.value)}
                            onBlur={handleHexInputCommit}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                handleHexInputCommit();
                              }
                            }}
                            className="flex-1 px-3 py-2 text-sm border border-border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <IconButton
                    variant="secondary"
                    size="sm"
                    layoutClassName={twMerge(
                      "h-8 w-8",
                      expandedColor === color.key
                        ? "self-start"
                        : "self-center",
                    )}
                    UNSAFE_className="opacity-0 group-hover:opacity-100 transition-opacity"
                    icon={
                      expandedColor === color.key ? (
                        <ChevronUp size={14} className="text-default" />
                      ) : (
                        <Pencil size={14} className="text-default" />
                      )
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditColor(color.key);
                    }}
                  />
                </div>
              </div>
            ))}
            {!hasAllBrandColors && (
              <div
                className="flex items-center gap-3 cursor-pointer p-2"
                onClick={() => void handleAddBrandColor()}
              >
                <div className="w-21 h-12 rounded-lg border-2 border-dashed border-border flex items-center justify-center flex-shrink-0">
                  <Plus size={16} className="text-muted" />
                </div>
                <div className="text-base font-medium p-1">
                  Add a brand color
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
